import { inheritRequestCorrelation } from '@brighthub/structured-logging';
import { Injectable, inject } from '@angular/core';
import { ApiService, GenericResponseCode } from '@brighthub/api';
import { Observable, map } from 'rxjs';
import { CarePlanAction } from '../actions.model';
import { ActionApi, ActionPage } from './action-api.token';
import {
  ActionApiEnvelope,
  ActionListPayload,
  ActionResource,
  CreateActionRequest,
  UpdateActionRequest,
} from './action-api.model';

const API_NAME = 'api-internal';
const ACTION_PATH = '/action';

@Injectable()
export class HttpActionApiService implements ActionApi {
  private readonly _api = inject(ApiService);

  listActions(
    caseId: string,
    limit: number,
    offset: number,
  ): Observable<ActionPage> {
    const numericCaseId = this._id(caseId, 'case');
    const params = new URLSearchParams();
    params.append('caseIds', String(numericCaseId));
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return this._list(params);
  }

  listClientActions(
    clientId: number,
    statusCodes: readonly string[],
    limit: number,
    offset: number,
  ): Observable<ActionPage> {
    const numericClientId = this._id(clientId, 'client');
    const params = new URLSearchParams();
    params.append('clientIds', String(numericClientId));
    statusCodes.forEach((statusCode) =>
      params.append('statusCodes', statusCode),
    );
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return this._list(params);
  }

  getAction(actionId: string): Observable<CarePlanAction> {
    const numericActionId = this._id(actionId, 'action');
    return this._api
      .get<
        ActionApiEnvelope<ActionResource>
      >(API_NAME, `${ACTION_PATH}/${numericActionId}`)
      .pipe(
        map((response) => this._payload(response)),
        map((resource) => this._action(resource)),
      );
  }

  createAction(body: CreateActionRequest): Observable<CarePlanAction> {
    return this._api
      .post<ActionApiEnvelope<ActionResource>>(API_NAME, ACTION_PATH, body, {
        suppressRequestBodyTelemetry: true,
        telemetryMetadata: {
          clientId: body.clientId,
          caseId: body.caseId,
          actionTypeCode: body.actionTypeCode,
          priorityCode: body.priorityCode,
          clientPromise: body.clientPromise,
        },
      })
      .pipe(
        map((response) => this._payload(response)),
        map((resource) => this._action(resource)),
      );
  }

  updateAction(
    actionId: string,
    body: UpdateActionRequest,
  ): Observable<CarePlanAction> {
    const numericActionId = this._id(actionId, 'action');
    return this._api
      .patch<ActionApiEnvelope<ActionResource>>(
        API_NAME,
        `${ACTION_PATH}/${numericActionId}`,
        body,
        {
          suppressRequestBodyTelemetry: true,
          telemetryMetadata: {
            actionId: numericActionId,
            operation: body.statusCode
              ? 'status-or-detail-update'
              : 'detail-update',
          },
        },
      )
      .pipe(
        map((response) => this._payload(response)),
        map((resource) => this._action(resource)),
      );
  }

  private _resources(payload: ActionListPayload): ActionResource[] {
    return payload.actions ?? payload.items ?? [];
  }

  private _list(params: URLSearchParams): Observable<ActionPage> {
    return this._api
      .get<
        ActionApiEnvelope<ActionListPayload>
      >(API_NAME, `${ACTION_PATH}?${params.toString()}`)
      .pipe(
        map((response) => this._payload(response)),
        map((payload) => {
          const resources = this._resources(payload);
          return {
            pagination: payload.pagination,
            consumedItems: resources.length,
            items: resources.flatMap((item) => {
              try {
                return [this._action(item)];
              } catch {
                return [];
              }
            }),
          };
        }),
      );
  }

  private _action(resource: ActionResource): CarePlanAction {
    const id = this._id(resource.actionId, 'action');
    const clientId = this._id(
      resource.client?.id ?? resource.clientId,
      'client',
    );
    const title = resource.name?.trim() ?? '';
    const actionTypeCode = this._code(resource.actionTypeCode);
    const statusCode = this._code(resource.statusCode);
    const priorityCode = this._code(resource.priorityCode);
    const assignedToId = resource.assignedToId?.trim() || null;
    return {
      id: String(id),
      clientId,
      caseId:
        resource.caseId == null
          ? null
          : String(this._id(resource.caseId, 'case')),
      title,
      description: resource.description?.trim() || null,
      actionTypeCode,
      assignedToId,
      statusCode,
      dueDate: resource.dueDate ?? null,
      priorityCode,
      sourceTypeCode: this._code(resource.sourceTypeCode),
      sourceId:
        resource.sourceId == null
          ? null
          : this._id(resource.sourceId, 'source'),
      communicationPreferenceCode:
        resource.communicationPreferenceCode?.trim().toUpperCase() || null,
      clientPromise: resource.clientPromise === true,
      completedAt: resource.completedAt ?? null,
      createdAt: resource.createdAt ?? null,
      updatedAt: resource.updatedAt ?? null,
    };
  }

  private _code(value: string | null | undefined): string | null {
    return value?.trim().toUpperCase() || null;
  }

  private _id(value: unknown, kind: string): number {
    const id = Number(value);
    if (!Number.isSafeInteger(id) || id <= 0) {
      throw new Error(`A valid ${kind} id is required.`);
    }
    return id;
  }

  private _payload<T>(response: ActionApiEnvelope<T>): T {
    if (
      response?.result?.code !== GenericResponseCode.SERVICE_OK ||
      response.payload == null
    ) {
      throw inheritRequestCorrelation(
        Object.assign(
          new Error(
            response?.result?.message ||
              'Action API returned an invalid response',
          ),
          { status: response?.result?.status },
        ),
        response,
      );
    }
    return response.payload;
  }
}
